import * as THREE from 'three';
import type { QuadDirection } from '../../state/useQuadViewStore';

/**
 * Quad view direction presets (Z-up world).
 *
 * Camera poses of the ortho quad cells; mirrors the main viewport's
 * ViewCommandHandler presets so _TOP/_FRONT/_RIGHT and these cells agree
 * exactly. Shared by MiniViewport (camera rig, pan basis) and the viewport
 * axes gizmo (orientation triad).
 */
export const QUAD_VIEW_DIRS: Record<
    QuadDirection,
    { position: [number, number, number]; up: [number, number, number] }
> = {
    top: { position: [0, 0, 1], up: [0, 1, 0] },
    bottom: { position: [0, 0, -1], up: [0, 1, 0] },
    front: { position: [-1, 0, 0], up: [0, 0, 1] },
    back: { position: [1, 0, 0], up: [0, 0, 1] },
    right: { position: [0, 1, 0], up: [0, 0, 1] },
    left: { position: [0, -1, 0], up: [0, 0, 1] },
};

/** Screen-space basis of a view: world right / up on screen plus the
    forward direction (into the screen). */
export function viewBasis(direction: QuadDirection): {
    right: THREE.Vector3;
    up: THREE.Vector3;
    forward: THREE.Vector3;
} {
    const base = QUAD_VIEW_DIRS[direction];
    const forward = new THREE.Vector3(-base.position[0], -base.position[1], -base.position[2]);
    const worldUp = new THREE.Vector3(base.up[0], base.up[1], base.up[2]);
    const right = new THREE.Vector3().crossVectors(forward, worldUp).normalize();
    const up = new THREE.Vector3().crossVectors(right, forward).normalize();
    return { right, up, forward };
}
